import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, MessageCircle, ShoppingBag, Package, Sprout, Truck } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { useCart } from "../context/CartContext";
import { PRODUCTS, CATEGORIES } from "../data/products";
import { buildEnquiryMessage, openWhatsApp } from "../lib/site";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const product = useMemo(() => PRODUCTS.find((p) => p.slug === slug), [slug]);
  const [selectedSize, setSelectedSize] = useState(product?.variants[0].size);
  const { add } = useCart();

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useEffect(() => { if (!product) nav("/shop", { replace: true }); }, [product, nav]);

  useEffect(() => {
    if (!product) return;
    const original = document.title;
    document.title = `${product.name} | Bhoomiza Organics`;
    return () => { document.title = original; };
  }, [product]);

  if (!product) return null;

  const variant = product.variants.find((v) => v.size === selectedSize) || product.variants[0];
  const cat = CATEGORIES.find((c) => c.id === product.category);
  const related = PRODUCTS.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);

  const handleAdd = () => add(product, variant, 1);
  const handleWhatsApp = () => openWhatsApp(buildEnquiryMessage(product.name, variant.size));

  return (
    <section className="product-page product-page-text">
      <nav aria-label="Breadcrumb" className="breadcrumb">
        <Link to="/"><ChevronLeft size={12} /> Home</Link>
        <span>/</span>
        <Link to="/shop">Shop</Link>
        <span>/</span>
        <Link to={`/shop/${product.category}`}>{cat?.label}</Link>
        <span>/</span>
        <span className="crumb-current">{product.name}</span>
      </nav>

      <motion.div className="pp-info-wide" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="chapter-tag"><em>Product</em> · {cat?.label}</p>
        <h1 className="page-h1" data-testid="pp-title">{product.name}</h1>
        <p className="pp-desc" data-testid="pp-desc">{product.description}</p>

        <fieldset className="pp-sizes">
          <legend>Pack Size</legend>
          <div className="pp-size-grid">
            {product.variants.map((v) => (
              <label key={v.size} className={selectedSize === v.size ? "psize psize-lg is-active" : "psize psize-lg"} data-testid={`pp-variant-${v.size}`}>
                <input type="radio" name="pp-size" value={v.size} checked={selectedSize === v.size} onChange={() => setSelectedSize(v.size)} />
                <span>{v.size}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="pp-actions">
          <button className="pbtn pbtn-primary pbtn-xl" onClick={handleAdd} data-testid="pp-add">
            <ShoppingBag size={18} /> Add to Cart
          </button>
          <button className="pbtn pbtn-ghost pbtn-xl" onClick={handleWhatsApp} data-testid="pp-wa">
            <MessageCircle size={18} /> Order on WhatsApp
          </button>
        </div>
        <p className="pp-wa-note">Prices, availability and shipping will be confirmed on WhatsApp. We'll share the final payable amount personally.</p>

        <ul className="pp-highlights">
          <li><Sprout size={14} /> Carefully selected ingredients</li>
          <li><Package size={14} /> Naturally processed, hygienically packed</li>
          <li><Truck size={14} /> Delivered across India · Confirmed via WhatsApp</li>
        </ul>
      </motion.div>

      <div className="pp-details">
        <div>
          <h2>About this product</h2>
          <p>{product.description}</p>
          <p>Made with carefully selected ingredients by Bhoomiza Organics — a Jaipur-based brand offering natural food products across India.</p>
        </div>
        <div>
          <h2>Ingredients</h2>
          <p>100% {product.name.toLowerCase()} — naturally processed. No additives.</p>
          <h2>Storage</h2>
          <p>Store in a cool, dry place away from direct sunlight. Reseal the pack after use to preserve freshness.</p>
        </div>
        <div>
          <h2>How to order</h2>
          <p>Select your preferred pack size and add it to your cart. When you're ready, tap <strong>Confirm Order on WhatsApp</strong> in the cart. We'll share pricing, availability and delivery details for your pincode.</p>
        </div>
      </div>

      {related.length > 0 && (
        <div className="pp-related">
          <h2>You may also like</h2>
          <div className="product-grid">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
          <div className="featured-cta"><Link to={`/shop/${product.category}`} className="primary-link">View all {cat?.label} <ArrowRight size={16} /></Link></div>
        </div>
      )}
    </section>
  );
}
